import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone } from 'lucide-react';
import { validateEmail } from '../../lib/validator';
import ContactusApi from './contactusApi';
import { Button } from '../../components/ui/button';
import CTASection from '../../home/sections/CTA';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {Textarea} from '../../components/ui/textarea';


interface FormState {
  message: string;
  senderEmail: string;
}

const ContactUsPage: React.FC = () => {
  const [formState, setFormState] = useState<FormState>({
    message: '',
    senderEmail: '',
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    setErrorMessage('');
    setEmailError('');
    let isValid = true;

    if (!formState.message.trim()) {
      setErrorMessage('Message is required');
      isValid = false;
    } else if (formState.message.length > 1000) {
      setErrorMessage('Message cannot exceed 1000 characters');
      isValid = false;
    }

    if (formState.senderEmail.trim()) {
      const emailResult = validateEmail(formState.senderEmail);
      if (!emailResult.isValid) {
        setEmailError(emailResult.error || 'Invalid email address format');
        isValid = false;
      }
    }

    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'message' && value.length > 1000) {
      setFormState((prev) => ({
        ...prev,
        [name]: value.slice(0, 1000),
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.trim()) {
      const emailResult = validateEmail(value);
      if (!emailResult.isValid) {
        setEmailError(emailResult.error || 'Invalid email address format');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await ContactusApi.create(formState.message, formState.senderEmail);
      setSubmitSuccess(true);
      setFormState({
        message: '',
        senderEmail: '',
      });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <>
      <Helmet>
        <title>Contact Us | Get in Touch with MrJoe.Pro Support</title>
        <meta name="description" content="Contact MrJoe.Pro for support, feedback, or questions about our AI-powered contractor software. Call 888-579-5808 or email mrjoe@mrjoe.pro. We're here to help!" />
        <meta name="keywords" content="contact MrJoe.Pro, contractor software support, customer service, help" />
        <link rel="canonical" href="https://mrjoe.pro/pub/contact_us" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mrjoe.pro/pub/contact_us" />
        <meta property="og:title" content="Contact Us | MrJoe.Pro" />
        <meta property="og:description" content="Get in touch with MrJoe.Pro. We're here to help with any questions about our contractor software." />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Us | MrJoe.Pro" />
        <meta name="twitter:description" content="Get in touch with MrJoe.Pro. We're here to help with any questions about our contractor software." />
      </Helmet>

      <section className="page-padding">
        <div className="container-safe">
          <h1 className="page-heading">Contact Us</h1>

          <div className="mx-auto mt-7 max-w-2xl">
            <p className="text-lg font-medium text-paragraph leading-relaxed">
              Your satisfaction is our highest priority. We value your feedback and would love to hear from you.
              Please share your thoughts in the text box below. If you'd like us to respond, kindly include your
              email address as well. Thank you!
            </p>

            {submitSuccess && (
              <div className="mt-6 rounded-lg bg-green-50 border border-green-200 p-4">
                <p className="text-green-800 font-semibold">
                  Thank you for your message! We will get back to you soon.
                </p>
              </div>
            )}

            {submitError && (
              <div className="mt-6 rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-red-800">{submitError}</p>
              </div>
            )}

            {!submitSuccess && (
              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div>
                  <Label htmlFor="message" className="block text-base font-medium text-foreground mb-2">
                    Message <span className="text-red-500">*</span>
                    <span className="text-sm text-gray-500 ml-2">(Maximum 1,000 characters)</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formState.message}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    maxLength={1000}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter your message here..."
                  />
                  {errorMessage && (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    {formState.message.length}/1000 characters
                  </p>
                </div>

                <div>
                  <Label htmlFor="senderEmail" >
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    id="senderEmail"
                    name="senderEmail"
                    value={formState.senderEmail}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="your.email@example.com"
                  />
                  {emailError && (
                    <p className="mt-2 text-sm text-red-600">{emailError}</p>
                  )}
                </div>

                <Button size="medium" variant="default"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto"
                >
                  <Mail className="size-5" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            )}

            <div className="mt-12 pt-8 border-t border-gray-200 space-y-4">
              <div className="flex items-center gap-3 text-lg">
                <Phone className="size-5 text-primary" />
                <span className="font-medium">Phone:</span>
                <a href="tel:888-579-5808" className="text-primary hover:underline">
                  888-579-5808
                </a>
              </div>

              <div className="flex items-center gap-3 text-lg">
                <Mail className="size-5 text-primary" />
                <span className="font-medium">Email:</span>
                <a href="mailto:mrjoe@mrjoe.pro" className="text-primary hover:underline">
                  mrjoe@mrjoe.pro
                </a>
              </div>


            </div>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );

};

export default ContactUsPage;